import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { apiErrorBody } from '@services/api/api-error';
import {
  HORIZON_DATE_RANGES,
  HorizonEngagementLevel,
  HorizonSummaryMetric,
  hasPeriodComparison,
} from '@modules/leads-management/lead-horizon-tracking/lead-horizon-tracking.model';
import { NgSelectModule } from '@ng-select/ng-select';
import moment from 'moment';
import { Subscription } from 'rxjs';

/** Horizon reports in Brisbane time (no daylight saving), so ranges and times use a fixed +10:00. */
const BRISBANE_UTC_OFFSET_MINUTES = 600;

interface HorizonUnitEngagement {
  available: boolean;
  unit_id: string;
  name: string;
  project: { project_id: string; name: string };
  summary: {
    unique_viewers: HorizonSummaryMetric;
    views: HorizonSummaryMetric;
    repeat_viewers: HorizonSummaryMetric;
    shortlists: HorizonSummaryMetric;
    information_requests: HorizonSummaryMetric;
    display_suite_bookings: HorizonSummaryMetric;
    eois: HorizonSummaryMetric;
  };
  leads: { lead_id: string; name: string; views: number; shortlisted: boolean; last_viewed_at: string; engagement_level: HorizonEngagementLevel }[];
}

/**
 * HorizonLotEngagementComponent
 *
 * Horizon demand for one lot, so the team can see how much buyer attention a lot gets and not just
 * its sales status. Draws nothing while the backend reports `available: false` (Phase 1) or fails.
 */
@Component({
  selector: 'app-horizon-lot-engagement',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './horizon-lot-engagement.component.html',
  styleUrls: ['./horizon-lot-engagement.component.scss'],
})
export class HorizonLotEngagementComponent implements OnChanges, OnDestroy {
  @Input() unitId: string | null = null;
  /** Set on a lead's Horizon tab: the figures and buyer list are then that one buyer's. */
  @Input() leadId: string | null = null;

  readonly dateRanges = HORIZON_DATE_RANGES;

  /** Same icons as Reporting → Horizon Tracking, so a figure looks the same wherever it appears. */
  readonly summaryCards = [
    { key: 'unique_viewers', label: 'Unique Viewers', icon: 'fa-solid fa-users' },
    { key: 'views', label: 'Views', icon: 'fa-regular fa-eye' },
    { key: 'repeat_viewers', label: 'Repeat Viewers', icon: 'fa-solid fa-rotate' },
    { key: 'shortlists', label: 'Shortlists', icon: 'fa-regular fa-heart' },
    { key: 'information_requests', label: 'Information Requests', icon: 'fa-regular fa-comment-dots' },
    { key: 'display_suite_bookings', label: 'Appointment Bookings', icon: 'fa-regular fa-calendar-check' },
    { key: 'eois', label: 'EOIs', icon: 'fa-solid fa-file-signature' },
  ];

  data: HorizonUnitEngagement | null = null;
  /** Set when the backend refuses the lot (`403 not_your_buyers`): none of this user's buyers viewed it. */
  isNotAllowed = false;
  selectedDays = 30;

  private subscription: Subscription;

  constructor(private horizon: HorizonTrackingService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitId'] || changes['leadId']) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  load(): void {
    this.subscription?.unsubscribe();
    this.data = null;
    this.isNotAllowed = false;
    if (!this.unitId) {
      return;
    }
    const today = moment().utcOffset(BRISBANE_UTC_OFFSET_MINUTES);
    const range = this.selectedDays
      ? {
          from: today
            .clone()
            .subtract(this.selectedDays - 1, 'days')
            .format('YYYY-MM-DD'),
          to: today.format('YYYY-MM-DD'),
        }
      : {};
    this.subscription = this.horizon.getUnitEngagement(this.unitId, range, this.leadId).subscribe({
      next: res => (this.data = res?.data?.available ? res.data : null),
      error: err => {
        this.data = null;
        // Agents only see lots their own buyers viewed or shortlisted (and only their own lead); admins see every lot.
        this.isNotAllowed = err?.status === 403 || apiErrorBody(err).data?.reason === 'not_your_buyers';
      },
    });
  }

  metric(key: string): HorizonSummaryMetric | null {
    const metric = this.data?.summary?.[key] as HorizonSummaryMetric;
    return metric && metric.available !== false ? metric : null;
  }

  hasComparison(metric: HorizonSummaryMetric): boolean {
    return hasPeriodComparison(metric);
  }

  formatDate(date: string): string {
    return moment(date).utcOffset(BRISBANE_UTC_OFFSET_MINUTES).format('D MMM, h:mma');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
